import { useEffect } from "react";

import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";
import type {
  AccountClassificationGroup,
  ClassificationGroupFormData,
} from "~/types/contabilidad";

const formSchema = z.object({
  company_id: z.string().min(1, "Seleccione una empresa"),
  code: z.string().min(1, "El codigo es requerido"),
  name: z.string().min(2, "El nombre debe tener al menos 2 caracteres"),
  orden: z.coerce.number().int().min(0, "El orden debe ser mayor o igual a 0"),
});

interface GrupoCuentaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group?: AccountClassificationGroup;
  onSubmit: (data: ClassificationGroupFormData) => void;
  isLoading?: boolean;
  defaultCompanyId?: string;
}

export function GrupoCuentaDialog({
  open,
  onOpenChange,
  group,
  onSubmit,
  isLoading = false,
  defaultCompanyId,
}: GrupoCuentaDialogProps) {
  const isEditing = !!group;
  const { data: companies = [], isLoading: isLoadingCompanies } = useCompanies();

  const form = useForm<ClassificationGroupFormData & { orden: number }>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      company_id: group?.company_id ?? defaultCompanyId ?? "",
      code: group?.code ?? "",
      name: group?.name ?? "",
      orden: group?.orden ?? 0,
    },
  });

  // Reset form when dialog opens with new data
  useEffect(() => {
    if (open) {
      form.reset({
        company_id: group?.company_id ?? defaultCompanyId ?? "",
        code: group?.code ?? "",
        name: group?.name ?? "",
        orden: group?.orden ?? 0,
      });
    }
  }, [open, group, defaultCompanyId, form]);

  const handleSubmit = (data: ClassificationGroupFormData & { orden: number }) => {
    onSubmit(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar Grupo de Cuenta" : "Nuevo Grupo de Cuenta"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Modifique los datos del grupo."
              : "Complete los datos para crear un nuevo grupo de cuenta."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="company_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Empresa *</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={isLoadingCompanies || isEditing}
                  >
                    <FormControl>
                      <SelectTrigger className="rounded-xl">
                        <SelectValue placeholder="Seleccione una empresa" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {companies.map((company) => (
                        <SelectItem key={company.id} value={company.id}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Codigo *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Ej: 1101, 2101"
                        className="rounded-xl"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="orden"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Orden *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="0"
                        className="rounded-xl"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Nombre del grupo de cuenta"
                      className="rounded-xl"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                className="rounded-full px-6"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Guardando...
                  </>
                ) : isEditing ? (
                  "Actualizar"
                ) : (
                  "Crear Grupo"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
